import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, Pagination, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  createShift, deleteShift, getActiveSessions, getPresenceSummaries,
  getShifts, updateShift,
  getTimezones, getTimezone,
  isVendorAdmin,
} from '../api/client';

const emptyForm = { name: '', start_time: '', end_time: '', timezone: 'Asia/Kolkata', is_active: true };

function apiTime(value) {
  if (!value) return value;
  const parts = value.split(':');
  return parts.length === 2 ? `${value}:00` : value;
}

function errorText(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg).join('; ');
  return error.message || 'Request failed';
}

function MetricCard({ title, value }) {
  return <Card sx={{ minWidth: 170 }}><CardContent>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
    <Typography color="text.secondary" variant="body2">{title}</Typography>
  </CardContent></Card>;
}

export default function ShiftsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState({ online: 0, away: 0, break: 0 });
  const [timezones, setTimezones] = useState([]);
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const result = await getShifts({ search: search || undefined, page,
        page_size: pageSize, paginated: true });
      // Current and older API deployments differ here: the paginated
      // endpoint may return either the envelope or the plain list.  The
      // table must consume the actual response body in both cases.
      const rows = Array.isArray(result)
        ? result
        : (result?.items || result?.results || result?.data || []);
      setItems(Array.isArray(rows) ? rows : []);
      setTotal(Array.isArray(result) ? rows.length : (result?.total || rows.length));
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.allSettled([getTimezones(), getTimezone()]).then(([all, org]) => {
      const values = all.status === 'fulfilled' ? (all.value?.timezones || all.value || []) : [];
      setTimezones(Array.isArray(values) ? values : []);
      if (org.status === 'fulfilled' && org.value?.timezone) {
        setForm(current => current.timezone === 'Asia/Kolkata'
          ? { ...current, timezone: org.value.timezone } : current);
      }
    });
  }, []);
  useEffect(() => {
    Promise.allSettled([getActiveSessions(), getPresenceSummaries()]).then(([sessions, presence]) => {
      const active = sessions.status === 'fulfilled' ? (sessions.value.items || sessions.value || []) : [];
      const summaries = presence.status === 'fulfilled' ? (presence.value || []) : [];
      setLive({
        online: active.filter(item => item.status === 'active').length,
        break: active.filter(item => item.status === 'break').length,
        away: summaries.filter(item => item.current_status === 'away').length,
      });
    });
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (shift) => {
    setEditing(shift);
    setForm({ name: shift.name, start_time: shift.start_time.slice(0, 5),
      end_time: shift.end_time.slice(0, 5), timezone: shift.timezone,
      is_active: shift.is_active });
    setDialogOpen(true);
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      const duplicate = items.some(item => item.id !== editing?.id
        && item.name.trim().toLowerCase() === form.name.trim().toLowerCase());
      if (duplicate) { setError('A shift with this name already exists.'); setSaving(false); return; }
      const payload = { ...form, start_time: apiTime(form.start_time), end_time: apiTime(form.end_time) };
      if (editing) await updateShift(editing.id, payload);
      else await createShift(payload);
      setDialogOpen(false); await load();
    } catch (e) { setError(errorText(e)); }
    finally { setSaving(false); }
  };
  const remove = async (shift) => {
    if (!window.confirm(`Delete shift ${shift.name}?`)) return;
    try { await deleteShift(shift.id); await load(); }
    catch (e) { setError(errorText(e)); }
  };

  return <Box sx={{ p: 3 }}>
    <Stack direction="row" justifyContent="space-between" mb={2}>
      <Typography variant="h5" fontWeight={700}>Shift Management</Typography>
      {isVendorAdmin() && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create Shift</Button>}
    </Stack>
    <Stack direction="row" spacing={2} sx={{ mb: 2, overflowX: 'auto' }}>
      {items.slice(0, 3).map(shift =>
        <MetricCard key={shift.id} title={shift.name} value={shift.assigned_operators} />)}
      <MetricCard title="Operators Online" value={live.online} />
      <MetricCard title="Operators Away" value={live.away} />
      <MetricCard title="Operators On Break" value={live.break} />
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <Paper sx={{ p: 2, mb: 2 }}>
      <TextField fullWidth size="small" label="Search shifts" value={search}
        onChange={event => { setSearch(event.target.value); setPage(1); }} />
    </Paper>
    <Paper>{loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box> :
      <Table size="small"><TableHead><TableRow>
        <TableCell>Name</TableCell><TableCell>Start</TableCell><TableCell>End</TableCell>
        <TableCell>Timezone</TableCell><TableCell align="right">Assigned Operators</TableCell>
        <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
      </TableRow></TableHead><TableBody>
        {items.map(shift => <TableRow key={shift.id} hover>
          <TableCell>{shift.name}</TableCell><TableCell>{shift.start_time}</TableCell>
          <TableCell>{shift.end_time}</TableCell><TableCell>{shift.timezone}</TableCell>
          <TableCell align="right">{shift.assigned_operators}</TableCell>
          <TableCell><Chip size="small" color={shift.is_active ? 'success' : 'default'}
            label={shift.is_active ? 'Active' : 'Inactive'} /></TableCell>
          <TableCell align="right">
            {isVendorAdmin() && <><Tooltip title="Edit"><IconButton onClick={() => openEdit(shift)}><EditIcon /></IconButton></Tooltip>
            <Tooltip title={shift.assigned_operators ? 'Unassign operators before deleting' : 'Delete'}>
              <span><IconButton color="error" disabled={shift.assigned_operators > 0}
                onClick={() => remove(shift)}><DeleteIcon /></IconButton></span>
            </Tooltip></>}
          </TableCell>
        </TableRow>)}
        {!items.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No shifts found</TableCell></TableRow>}
      </TableBody></Table>}
    </Paper>
    {total > pageSize && <Stack alignItems="center" sx={{ mt: 2 }}>
      <Pagination page={page} count={Math.ceil(total / pageSize)} onChange={(_, value) => setPage(value)} />
    </Stack>}
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Edit Shift' : 'Create Shift'}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
        <TextField required label="Name" value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
        <Stack direction="row" spacing={2}>
          <TextField required fullWidth type="time" label="Start" InputLabelProps={{ shrink: true }}
            value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} />
          <TextField required fullWidth type="time" label="End" InputLabelProps={{ shrink: true }}
            value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} />
        </Stack>
        <TextField required select label="Time Zone" value={form.timezone}
          onChange={e => setForm({ ...form, timezone: e.target.value })}
          helperText="Select an IANA timezone" SelectProps={{ native: true }}>
          <option value="">Select timezone</option>
          {(timezones.length ? timezones : ['Asia/Kolkata', 'America/New_York', 'Europe/London']).map(tz =>
            <option key={tz} value={tz}>{tz}</option>)}
        </TextField>
        {editing && <TextField select label="Status" value={form.is_active}
          onChange={e => setForm({ ...form, is_active: e.target.value === 'true' })} SelectProps={{ native: true }}>
          <option value="true">Active</option><option value="false">Inactive</option>
        </TextField>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.name || !form.start_time || !form.end_time || !form.timezone}
          onClick={save}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
    </Dialog>
  </Box>;
}
